import { initializeErrorReporting } from '@eventbrite/error-reporting';
import {
    getLocaleOverride,
    getParentLocale,
    setupAsync,
} from '@eventbrite/i18n';
import { Event } from '@sentry/types';
import React from 'react';
import { hydrate, render } from 'react-dom';
import { App } from './app';
import { getShareComponentProps } from './components/ShareEvent/ShareEventRoot';
import { ServerApp } from './server';
import { initializeStatsigSdk } from './statsig';
import './styles/listings.scss';

import('intersection-observer');

// retrieve data from server to hydrate on the client
const props = window.__SERVER_DATA__ || ({} as any);

const parentLocale = getParentLocale(
    getLocaleOverride() || props.env.localeInfo.locale,
);

const SENTRY_TRACES_SAMPLE_RATE = 0.5;

const ignoredErrors = [
    /Script error for "analyticsjs"/i,
    /Script error for "grylls"/i,
    /Script error for "grylls_config"/i,
    /Load timeout for modules: grylls/i,
    /heap.resetIdentity is not a function/i,
    /Error calling eventclick/i,
    /al_onPoststitialDismiss/i,
    /al_onAdViewRendered/i,
    /TypeError: Load failed/i,
];

const ignoreError = (event: Event) => {
    const errorMessage = event?.exception?.values?.[0]?.value;

    if (errorMessage) {
        const isAnIgnoredEvent =
            ignoredErrors.findIndex((ignoredError) =>
                ignoredError.test(errorMessage),
            ) !== -1;

        return isAnIgnoredEvent;
    }

    return false;
};

const main = async () => {
    const serverAppProps = {
        event: props.event,
        env: props.env,
        request: props.request,
        footerLinks: props.footerLinks,
        eventDescription: props.components.eventDescription,
        eventDetails: props.components.eventDetails,
        organizer: props.organizer,
        settings: props.settings,
        tags: props.components.tags,
        shareInline: props.components.shareInline,
        organizerPanelLayout: props.components.organizerPanel,
        eventMapLayout: props.components.eventMap,
        conversionBar: props.components.conversionBar,
        eventHero: props.components.eventHero,
        user: props.user,
        mainBodyBackground: props.components.mainBodyBackground,
        notificationBar: props.components.notificationBar,
        expiredEventsPage: {
            ...props.components.relatedEventsExpiredPage,
            shareComponentProps: getShareComponentProps(props),
        },
    };

    try {
        initializeErrorReporting({
            dsn: props.settings.sentryDNS,
            tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
            ignoreError,
            metaData: {
                tags: {
                    event_id: props.event.id,
                },
            },
        });

        if (parentLocale !== 'en_US') {
            await setupAsync({
                parentLocale,
                translations:
                    /* webpackChunkName: "listings-i18n-translation" */ import(
                        `./i18n/translations/${parentLocale}.json`
                    ),
            });
        }
        await initializeStatsigSdk(props);
    } finally {
        const rootElement = document.getElementById('root');
        if (rootElement) {
            hydrate(
                <ServerApp {...serverAppProps} />,
                document.getElementById('root'),
            );
        }
        render(<App {...props} />, document.getElementById('listings-root'));
    }
};
main();
